import React, { useCallback, useEffect, useState } from 'react';

export const MONITOR_ZOOM_STORAGE_KEY = 'nbr.monitorZoom';
export const MONITOR_ZOOM_MIN = 75;
export const MONITOR_ZOOM_MAX = 150;
export const MONITOR_ZOOM_STEP = 10;

export function readMonitorZoom() {
    try {
        const n = parseInt(localStorage.getItem(MONITOR_ZOOM_STORAGE_KEY), 10);
        if (Number.isNaN(n)) return 100;
        return Math.min(MONITOR_ZOOM_MAX, Math.max(MONITOR_ZOOM_MIN, n));
    } catch {
        return 100;
    }
}

export function writeMonitorZoom(pct) {
    try {
        localStorage.setItem(MONITOR_ZOOM_STORAGE_KEY, String(pct));
    } catch {
        /* ignore quota */
    }
}

export function clampMonitorZoom(pct) {
    return Math.min(MONITOR_ZOOM_MAX, Math.max(MONITOR_ZOOM_MIN, pct));
}

export function getMonitorCanvasZoomStyle(pct) {
    const scale = clampMonitorZoom(pct) / 100;
    return {
        zoom: scale,
        WebkitTextSizeAdjust: '100%'
    };
}

export function useMonitorZoom() {
    const [zoomPct, setZoomPct] = useState(() => readMonitorZoom());

    useEffect(() => {
        writeMonitorZoom(zoomPct);
    }, [zoomPct]);

    const zoomOut = useCallback(() => {
        setZoomPct((v) => clampMonitorZoom(v - MONITOR_ZOOM_STEP));
    }, []);

    const zoomIn = useCallback(() => {
        setZoomPct((v) => clampMonitorZoom(v + MONITOR_ZOOM_STEP));
    }, []);

    return { zoomPct, zoomOut, zoomIn, canvasStyle: getMonitorCanvasZoomStyle(zoomPct) };
}

export function useMonitorRoomsOnly() {
    const [roomsOnly, setRoomsOnly] = useState(false);

    useEffect(() => {
        document.body.classList.toggle('monitor-rooms-only', roomsOnly);
        document.documentElement.classList.toggle('monitor-rooms-only', roomsOnly);
        if (roomsOnly) {
            document.documentElement.setAttribute('data-monitor-rooms-only', 'true');
        } else {
            document.documentElement.removeAttribute('data-monitor-rooms-only');
        }
        document.getElementById('main-wrapper')?.classList.toggle('monitor-rooms-only', roomsOnly);
        return () => {
            document.body.classList.remove('monitor-rooms-only');
            document.documentElement.classList.remove('monitor-rooms-only');
            document.documentElement.removeAttribute('data-monitor-rooms-only');
            document.getElementById('main-wrapper')?.classList.remove('monitor-rooms-only');
        };
    }, [roomsOnly]);

    useEffect(() => {
        if (!roomsOnly) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setRoomsOnly(false);
                if (document.fullscreenElement) {
                    document.exitFullscreen?.().catch(() => {});
                }
            }
        };
        // Do NOT exit rooms-only when the browser Fullscreen API ends.
        // On iPad Safari, swipe-to-scroll (and chrome gestures) often leave
        // document.fullscreenElement while the user still wants the kiosk layout.
        // Rooms-only CSS mode is independent and is cleared only via Exit / Escape.
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
        };
    }, [roomsOnly]);

    const enterRoomsOnly = useCallback(async () => {
        setRoomsOnly(true);
        const root = document.documentElement;
        try {
            // Optional enhancement when supported; rooms-only UI does not depend on it.
            if (root.requestFullscreen) {
                await root.requestFullscreen();
            }
        } catch {
            /* iOS Safari often rejects Fullscreen API — CSS rooms-only still applies */
        }
    }, []);

    const exitRoomsOnly = useCallback(async () => {
        setRoomsOnly(false);
        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            }
        } catch {
            /* ignore */
        }
    }, []);

    const toggleRoomsOnly = useCallback(() => {
        if (roomsOnly) {
            exitRoomsOnly();
        } else {
            enterRoomsOnly();
        }
    }, [roomsOnly, enterRoomsOnly, exitRoomsOnly]);

    return { roomsOnly, enterRoomsOnly, exitRoomsOnly, toggleRoomsOnly };
}

export function MonitorWorkspaceControls({
    roomsOnly,
    onToggleRoomsOnly,
    zoomPct,
    onZoomOut,
    onZoomIn,
    floating = false,
    title = ''
}) {
    return (
        <div className={`monitor-workspace-controls${floating ? ' monitor-workspace-controls--float' : ''}`}>
            {floating && title ? (
                <span className="monitor-workspace-controls__title text-truncate">{title}</span>
            ) : null}
            <div className="btn-group btn-group-sm" role="group" aria-label="Page zoom">
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={onZoomOut}
                    disabled={zoomPct <= MONITOR_ZOOM_MIN}
                    title="Zoom out"
                >
                    −
                </button>
                <span className="btn btn-outline-secondary disabled" style={{ pointerEvents: 'none', opacity: 1 }}>
                    {zoomPct}%
                </span>
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={onZoomIn}
                    disabled={zoomPct >= MONITOR_ZOOM_MAX}
                    title="Zoom in"
                >
                    +
                </button>
            </div>
            <button
                type="button"
                className={`btn btn-sm ${roomsOnly ? 'btn-warning' : 'btn-outline-dark'}`}
                onClick={onToggleRoomsOnly}
            >
                <i className={`bi ${roomsOnly ? 'bi-fullscreen-exit' : 'bi-arrows-fullscreen'} me-1`} aria-hidden />
                {roomsOnly ? 'Exit Full Screen' : 'Full Screen'}
            </button>
        </div>
    );
}
